/**
 * @fileoverview Singer Profile Form component.
 * @summary Form for editing singer profile (firstName, lastName, pseudo).
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/ProfileFormSinger
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import FloatingLabelInput from '../../../components/FloatingLabelInput'
import Spinner from '../../../components/Spinner'

interface ProfileFormSingerProps {
  firstName: string
  lastName: string
  pseudo: string
  email: string
  pendingEmail?: string
  isSaving: boolean
  onFirstNameChange: (value: string) => void
  onLastNameChange: (value: string) => void
  onPseudoChange: (value: string) => void
  onEmailChange: (value: string) => void
  onSave: () => void
}

/**
 * @summary Singer profile form component.
 * @param {ProfileFormSingerProps} props - Component props.
 * @returns {React.ReactElement} Singer profile form.
 */
export function ProfileFormSinger({
  firstName,
  lastName,
  pseudo,
  email,
  pendingEmail,
  isSaving,
  onFirstNameChange,
  onLastNameChange,
  onPseudoChange,
  onEmailChange,
  onSave,
}: ProfileFormSingerProps) {
  const { t } = useTranslation()

  return (
    <div className="card bg-base-100 dark:bg-base-800 shadow-lg">
      <div className="card-body">
        <h2 className="card-title text-xl mb-4">{t('singer.editProfile')}</h2>

        <div className="space-y-4">
          <FloatingLabelInput
            id="firstName"
            name="firstName"
            label={t('auth.firstName')}
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
          />

          <FloatingLabelInput
            id="pseudo"
            name="pseudo"
            label={t('auth.pseudo')}
            value={pseudo}
            onChange={(e) => onPseudoChange(e.target.value)}
          />

          <FloatingLabelInput
            id="lastName"
            name="lastName"
            label={t('auth.lastName')}
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
          />

          <FloatingLabelInput
            id="email"
            name="email"
            label={t('auth.email')}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
          />

          {pendingEmail && (
            <div className="p-3 bg-warning/10 border border-warning rounded-lg">
              <p className="text-sm text-warning font-semibold">⏳ {t('email.validationPending')}</p>
              <p className="text-xs text-warning/80 mt-1">{t('email.pendingEmailAddress')}: {pendingEmail}</p>
            </div>
          )}

          <button
            className="btn btn-primary w-full"
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? <Spinner size="sm" className="text-white!" /> : t('singer.saveChanges')}
            
          </button>
        </div>
      </div>
    </div>
  )
}

